import React, { useEffect } from "react";
import { toast } from "react-toastify";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  return apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
}

function maskSecret(secret) {
  const value = String(secret || "");
  if (!value) return "";
  if (value.length <= 8) return "*".repeat(value.length);
  return `${value.slice(0, 4)}${"*".repeat(Math.max(0, value.length - 8))}${value.slice(-4)}`;
}

function pickVersionKeyFromApiData(data) {
  if (!data || typeof data !== "object") return "";
  const primary = String(data.versionKey ?? "").trim();
  if (primary) return primary;
  return String(data.V1 ?? "").trim();
}

export default function SecretKeyManagerPage() {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [lastGeneratedSecret, setLastGeneratedSecret] = React.useState("");
  const [lastVersionKey, setLastVersionKey] = React.useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const baseUrl = getBaseUrl();
        const res = await fetch(`${baseUrl}/api/get-secret-key`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let payload = {};
        try {
          payload = await res.json();
        } catch {
          payload = {};
        }
        if (!res.ok || !payload?.success || !payload?.data) return;
        const vk = pickVersionKeyFromApiData(payload.data);
        const fetchedSecret = String(payload.data.secret || "").trim();
        if (!cancelled) {
          if (vk) setLastVersionKey(vk);
          if (fetchedSecret) setLastGeneratedSecret(fetchedSecret);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      const token = localStorage.getItem("token");
      const baseUrl = getBaseUrl();
      const res = await fetch(`${baseUrl}/api/create-secret-key`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      let payload = {};
      try {
        payload = await res.json();
      } catch {
        payload = {};
      }
      if (!res.ok || !payload?.success || !payload?.data?.secret) {
        throw new Error(payload?.message || "Failed to generate secret key");
      }
      const generated = String(payload.data.secret);
      setLastGeneratedSecret(generated);
      setLastVersionKey(pickVersionKeyFromApiData(payload.data));
      toast.success("Secret key generated. Copy and keep it safe.");
    } catch (error) {
      toast.error(error?.message || "Unable to generate secret key");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!lastGeneratedSecret) {
      toast.error("Generate a secret key first.");
      return;
    }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(lastGeneratedSecret);
      } else {
        // Fallback for non-secure (HTTP) contexts where navigator.clipboard is unavailable.
        const textarea = document.createElement("textarea");
        textarea.value = lastGeneratedSecret;
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        if (!ok) throw new Error("execCommand copy failed");
      }
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed. Please try again.");
    }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 4px 18px rgba(0,0,0,0.06)" }}>
        <h3 style={{ margin: 0, color: "#8b1634" }}>Secret Key Manager</h3>
        <p style={{ marginTop: 10, color: "#64748b" }}>
          Issue a new import encryption key for secure encrypted form files. The active key takes effect immediately and is required for all subsequent imports.
        </p>

        <div style={{ marginTop: 18, border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, background: "#f8fafc" }}>
          <div style={{ fontWeight: 700, color: "#1f2937", marginBottom: 8 }}>Active secret key</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              value={
                lastGeneratedSecret
                  ? maskSecret(lastGeneratedSecret)
                  : "Load the page to fetch the current key, or generate a new one"
              }
              readOnly
              style={{
                flex: 1,
                minWidth: 300,
                border: "1px solid #cbd5e1",
                borderRadius: 10,
                height: 42,
                padding: "0 12px",
                background: "#fff",
                color: "#334155",
                fontWeight: 600,
              }}
            />
            <button
              type="button"
              onClick={handleCopy}
              disabled={!lastGeneratedSecret}
              style={{
                background: lastGeneratedSecret ? "#0f766e" : "#94a3b8",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                height: 42,
                padding: "0 18px",
                cursor: lastGeneratedSecret ? "pointer" : "not-allowed",
                fontWeight: 700,
              }}
            >
              Copy Key
            </button>
          </div>
        </div>

        {lastVersionKey ? (
          <div style={{ marginTop: 14, border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, background: "#f8fafc" }}>
            <div style={{ fontWeight: 700, color: "#1f2937", marginBottom: 8 }}>versionKey (add to JSON before encryption)</div>
            <input
              type="text"
              value={lastVersionKey}
              readOnly
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1px solid #cbd5e1",
                borderRadius: 10,
                height: 42,
                padding: "0 12px",
                background: "#fff",
                color: "#334155",
                fontWeight: 600,
              }}
            />
          </div>
        ) : null}

        <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              background: isGenerating ? "#94a3b8" : "#8b1634",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              height: 44,
              padding: "0 18px",
              cursor: isGenerating ? "not-allowed" : "pointer",
              fontWeight: 700,
            }}
          >
            {isGenerating ? "Generating..." : "Generate Secret Key"}
          </button>
        </div>
      </div>
    </div>
  );
}
